import { Decorator, FormApi } from 'final-form';
import { getIn } from 'final-form';

// FORK OF: https://github.com/final-form/final-form-focus

/**
 * Finds the input by looking if the name attribute path is existing in the errors object
 */
const defaultFindInput = (inputs, errors: Record<string, unknown>) =>
	inputs.find((input) => input.name && getIn(errors, input.name));

export interface FocusableInput {
	name: string;
	focus: () => void;
}

export type GetInputs = () => FocusableInput[];
export type FindInput = (inputs: FocusableInput[], errors: Record<string, unknown>) => FocusableInput | undefined;

export const createDecorator = (getInputs?, findInput?): Decorator => {
	return (form: FormApi) => {
		const focusOnFirstError = (errors: Record<string, unknown>) => {
			if (!getInputs) {
				getInputs = getAllInputs;
			}
			if (!findInput) {
				findInput = defaultFindInput;
			}
			const firstInput = findInput(getInputs(), errors);
			if (firstInput) {
				/* 
                 this is the only customization we added. this allows the .focus() call to happen
                 after react is done rendering to make sure validation errors are highlighted
                 across multiple tabs.
                 */

				setTimeout(() => {
					firstInput.focus();
				});
			}
		};

		// Save original submit function
		const originalSubmit = form.submit;
		// Subscribe to errors, and keep a local copy of them
		let state: {
			errors?: Record<string, unknown>;
			submitErrors?: Record<string, unknown>;
		} = {};
		const unsubscribe = form.subscribe(
			(nextState) => {
				state = nextState;
			},
			{ errors: true, submitErrors: true },
		);
		// What to do after submit
		const afterSubmit = () => {
			const { errors, submitErrors } = state;
			if (errors && Object.keys(errors).length) {
				focusOnFirstError(errors);
			} else if (submitErrors && Object.keys(submitErrors).length) {
				focusOnFirstError(submitErrors);
			}
		};
		// Rewrite submit function
		form.submit = () => {
			const result = originalSubmit.call(form);
			if (result && typeof result.then === 'function') {
				// async
				result.then(afterSubmit);
			} else {
				// sync
				afterSubmit();
			}
			return result;
		};
		return () => {
			unsubscribe();
			form.submit = originalSubmit;
		};
	};
};

/**
 * Gets all the inputs inside all forms on the page
 */
const getAllInputs = () => {
	if (typeof document === 'undefined') {
		return [];
	}
	return Array.prototype.slice
		.call(document.forms)
		.reduce(
			(accumulator, form) => accumulator.concat(Array.prototype.slice.call(form).filter(isFocusableInput)),
			[],
		);
};

/**
 * Predicate to identify inputs that can have focus() called on them
 */
const isFocusableInput = (wtf: any) => !!(wtf && typeof wtf.focus === 'function');
